import type { ApiError } from '@agency/contracts';

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiError['code'] | 'NETWORK',
    message: string,
    readonly requestId?: string,
    readonly fieldErrors: ApiError['fieldErrors'] = [],
    readonly retryable = false,
  ) {
    super(message);
  }
}

let csrfToken = '';
export const setCsrf = (t: string) => { csrfToken = t; };

/** Fetch wrapper: same-origin, cookie session, CSRF header on mutations, typed error contract. */
export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: { ...(body !== undefined ? { 'content-type': 'application/json' } : {}), ...(method !== 'GET' ? { 'x-csrf-token': csrfToken } : {}) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiClientError(0, 'NETWORK', 'Cannot reach the server. Check your connection; unsaved work is kept on this page.', undefined, [], true);
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const e = (data as { error?: ApiError } | null)?.error;
    throw new ApiClientError(res.status, e?.code ?? 'INTERNAL', e?.message ?? 'Unexpected response from the server.', e?.requestId, e?.fieldErrors ?? [], e?.retryable ?? res.status >= 500);
  }
  return data as T;
}
export const get = <T,>(p: string) => api<T>('GET', p);
export const post = <T,>(p: string, b?: unknown) => api<T>('POST', p, b ?? {});
export const put = <T,>(p: string, b: unknown) => api<T>('PUT', p, b);
export const patch = <T,>(p: string, b: unknown) => api<T>('PATCH', p, b);
export const del = <T = void,>(p: string) => api<T>('DELETE', p);

/** Raw file upload: the bytes are the body, the type and original name travel in headers. */
export async function upload<T>(path: string, file: File): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': file.type, 'x-filename': encodeURIComponent(file.name), 'x-csrf-token': csrfToken }, body: file });
  } catch {
    throw new ApiClientError(0, 'NETWORK', 'Cannot reach the server. Check your connection and try again.', undefined, [], true);
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const e = (data as { error?: ApiError } | null)?.error;
    throw new ApiClientError(res.status, e?.code ?? 'INTERNAL', e?.message ?? 'Upload failed.', e?.requestId, e?.fieldErrors ?? [], res.status >= 500);
  }
  return data as T;
}
